import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Box, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import Popper, { PopperProps } from '@mui/material/Popper';
import { LoadingButton } from '@mui/lab';
import { ILabel } from '@trii/types/dist/Contacts';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslationContext } from '../../context/TranslationContext';

type TagsEditorProps = {
  value: ILabel[];
  options: ILabel[];
  onChange: (value: ILabel[]) => void;
  onBack: () => void;
  onSave: () => void;
  isSaving: boolean;
  onCreateNew: (name: string) => void;
  isCreatingNew: boolean;
  popperContainer?: HTMLElement | null;
};

const PAGE_SIZE = 20;

const AutocompletePopper = (props: PopperProps & { popperContainer?: HTMLElement | null }) => {
  const { popperContainer, ...rest } = props;
  return (
    <Popper
      {...rest}
      container={popperContainer ?? rest.container}
      style={{ ...(rest.style ?? {}), zIndex: 20000 }}
    />
  );
};

const TagsEditor = ({
  value,
  options,
  onChange,
  onBack,
  onSave,
  isSaving,
  onCreateNew,
  isCreatingNew,
  popperContainer,
}: TagsEditorProps) => {
  const t = useTranslationContext();

  const [searchValue, setSearchValue] = useState<string>('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  const deferredSearchValue = useDeferredValue(searchValue);

  const safeOptions = useMemo(() => options ?? [], [options]);

  const filteredOptions = useMemo(() => {
    const q = String(deferredSearchValue ?? '')
      .trim()
      .toLowerCase();
    if (!q) return safeOptions;

    return safeOptions.filter((o) =>
      String(o?.name ?? '')
        .toLowerCase()
        .includes(q),
    );
  }, [safeOptions, deferredSearchValue]);

  const visibleOptions = useMemo(() => filteredOptions.slice(0, limit), [filteredOptions, limit]);

  const optionsWithSelectedValues = useMemo(() => {
    const byId = new Map<string, ILabel>();

    for (const selected of value ?? []) {
      if (!selected?.id) continue;
      byId.set(String(selected.id), selected);
    }

    for (const opt of visibleOptions ?? []) {
      if (!opt?.id) continue;
      if (byId.has(String(opt.id))) continue;
      byId.set(String(opt.id), opt);
    }

    return Array.from(byId.values());
  }, [value, visibleOptions]);

  const handleSearchChange = (_: unknown, newInputValue: string) => {
    setSearchValue(newInputValue ?? '');
    setLimit(PAGE_SIZE);
  };

  const onListboxScroll = (event: unknown) => {
    const element = event as HTMLElement;
    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 24;
    if (nearBottom) setLimit((l) => l + PAGE_SIZE);
  };

  const CustomNoOptionsMessage = () => {
    const trimmedSearchValue = String(searchValue ?? '').trim();
    const isDisabled = !trimmedSearchValue || isSaving || isCreatingNew;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {t('global.noOptions')}
        <LoadingButton
          onClick={() => onCreateNew(trimmedSearchValue)}
          loading={isCreatingNew}
          disabled={isDisabled}
          variant="contained"
          size="small"
          sx={{ marginTop: 1.3 }}
        >
          {t('global.createNew')}
        </LoadingButton>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={onBack} aria-label={t('global.back')} disabled={isSaving}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1">{t('tags.label')}</Typography>

        <Box sx={{ flex: 1 }} />

        <LoadingButton
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={onSave}
          loading={isSaving}
          disabled={isSaving}
        >
          {t('global.save')}
        </LoadingButton>
      </Stack>

      <Autocomplete<ILabel, true, false, false>
        multiple
        disablePortal={false}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={optionsWithSelectedValues}
        filterOptions={(x) => x}
        inputValue={searchValue}
        onInputChange={handleSearchChange}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
        PopperComponent={(props) => (
          <AutocompletePopper {...props} popperContainer={popperContainer} />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props as any;
          return (
            <li {...rest} key={option?.id ? String(option.id) : String(key ?? option?.name ?? '')}>
              {option.name ?? ''}
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const tagProps = getTagProps({ index }) as any;
            const { key, ...rest } = tagProps;
            return (
              <Chip
                key={option?.id ? String(option.id) : String(key ?? option?.name ?? index)}
                label={option.name ?? ''}
                {...rest}
              />
            );
          })
        }
        size="small"
        renderInput={(params) => <TextField {...params} size="small" />}
        ListboxProps={{
          onScroll: onListboxScroll as any,
          sx: { maxHeight: 280, overflowY: 'auto' },
        }}
        noOptionsText={<CustomNoOptionsMessage />}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default TagsEditor;
